import { useContext, useEffect, useMemo, useState } from 'react';
import ModalContext from '../../../../../contexts/ModalContext';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { createGroup, CreateGroupRequestPayload } from '../../../../../api/my-groups';
import useStudioGroup from '../../../../../hooks/useStudioGroup';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { fetchTeacherProfile, TeacherSchool } from '../../../../../api/my-account';
import { sortByName } from '../../../../../utils/sortByName';
import { SchoolYear } from '../../../hooks/useGroups';
import { useDefaultToast } from '../../../../../hooks/useDefaultToast';
import Alert from '@pelckmans/elementis/components/alert';
import AlertGroup from '@pelckmans/elementis/components/alert-group';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Heading from '@pelckmans/elementis/components/heading';
import SelectField from '@pelckmans/elementis/components/select-field';
import TextField from '@pelckmans/elementis/components/text-field';

interface Props {
  id: string;
  selectedSchoolYear: SchoolYear;
  onGroupAdded: () => void;
}

interface FormValues {
  courseName: string;
  school: TeacherSchool;
}

export default function AddGroupModal({ id, selectedSchoolYear, onGroupAdded }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const studioGroup = useStudioGroup();
  const handleError = useThrowAsyncError();
  const [schools, setSchools] = useState<TeacherSchool[]>([]);
  const [searchValue, onSearchValueChange] = useState('');
  const { failedToast, successToast } = useDefaultToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    mode: 'all',
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const payload: CreateGroupRequestPayload = {
        name: data.courseName,
        schoolYear: `${selectedSchoolYear.startYear}-${selectedSchoolYear.endYear}`,
        school: {
          id: data.school.id,
          isSmartschool: false,
        },
        subjects: [],
      };
      await createGroup(payload, { studioGroup });
      onGroupAdded();
      successToast();
      closeModal(id);
    } catch (err) {
      failedToast();
    }
  };

  useEffect(() => {
    const getSchools = async () => {
      const data = await fetchTeacherProfile();
      setSchools(data.schools);
    };
    getSchools().catch(handleError);
  }, [handleError]);

  const sortedSchools = useMemo(() => schools.sort(sortByName(language)), [language, schools]);
  const filteredSchools = useMemo(
    () => sortedSchools.filter(school => school.name.toLowerCase().includes(searchValue.trim().toLowerCase())),
    [searchValue, sortedSchools],
  );

  return (
    <Dialog id={id} aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.A11Y_LABEL')} modal>
      <Dialog.Header
        aria-label={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.CLOSE.A11Y_LABEL')}
        onClose={() => closeModal(id)}
        title={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.TITLE', {
          schoolYearStartYear: selectedSchoolYear.startYear,
          schoolYearEndYear: selectedSchoolYear.endYear,
        })}
      />
      <Dialog.Body>
        <div className="add-vo-group">
          {isSubmitted && !isValid && (
            <AlertGroup aria-label={undefined} role={undefined}>
              <Alert prominence="danger">{t('ALERTS.INVALID.MESSAGE')}</Alert>
            </AlertGroup>
          )}
          <form id="form-add-group" onSubmit={handleSubmit(onSubmit)} className="add-vo-group-form">
            <Heading level={4} removeSpacing="all">
              {t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.TITLE')}
            </Heading>
            <TextField
              id="courseName"
              key="courseName"
              labelText={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.COURSE_NAME.LABEL')}
              autoComplete="on"
              required
              placeholder={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.COURSE_NAME.PLACEHOLDER')}
              error={!!errors.courseName}
              errorText={errors.courseName?.message}
              {...register('courseName', {
                required: t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.COURSE_NAME.ERROR_REQUIRED'),
              })}
            />
            <Controller
              name="school"
              control={control}
              rules={{ required: t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.SCHOOL.ERROR_REQUIRED') }}
              render={({ field, fieldState }) => (
                <SelectField
                  labelText={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.SCHOOL.LABEL')}
                  required
                  placeholder={t('PAGES.MY_GROUPS.MODALS.ADD_GROUP.FORM.SCHOOL.PLACEHOLDER')}
                  renderValueContent={school => (school ? school.name : '')}
                  dropdownMenuProps={{
                    ariaLabel: undefined,
                  }}
                  error={!!fieldState.error}
                  errorText={fieldState.error?.message}
                  value={field.value}
                  onChange={school => field.onChange(school)}
                  onBlur={field.onBlur}
                  searchable
                  searchProps={{
                    searchValue,
                    onSearchValueChange,
                    inputProps: {
                      ariaLabel: t('PAGES.MY_GROUPS.MODALS.ADD_PERSONAL_GROUP.BODY.FORM.SCHOOL.SEARCH.A11Y_LABEL'),
                    },
                  }}
                >
                  {filteredSchools.map(school => (
                    <SelectField.Item key={school.id} text={school.name} value={school} />
                  ))}
                </SelectField>
              )}
            />
          </form>
        </div>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={() => closeModal(id)}>
              {t('ACTIONS.CANCEL')}
            </Button>
            <Button prominence="success" key="create" type="submit" form="form-add-group">
              {t('ACTIONS.CREATE')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
